import React from 'react';
import { Coins, AlertTriangle } from 'lucide-react';

const TokenMeter = ({ tokens = 0, maxTokens = 1000, className = '' }) => {
  const percentage = Math.max((tokens / maxTokens) * 100, 0);
  const isLow = tokens <= 100;
  const isEmpty = tokens <= 0;

  const getColorClasses = () => {
    if (isEmpty) return 'text-red-600 bg-red-50 border-red-200';
    if (isLow) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-primary-600 bg-primary-50 border-primary-200';
  };

  const getProgressColor = () => {
    if (isEmpty) return 'bg-red-500';
    if (isLow) return 'bg-orange-500';
    return 'bg-primary-500';
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Mobile: Compact version */}
      <div className="sm:hidden flex items-center space-x-1">
        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border ${getColorClasses()}`}>
          {isEmpty && <AlertTriangle className="w-3 h-3" />}
          {!isEmpty && <Coins className="w-3 h-3" />}
          <span className="text-xs font-medium">{tokens}</span>
        </div>
      </div>

      {/* Desktop: Full version */}
      <div className="hidden sm:flex items-center space-x-2 md:space-x-3">
        <div className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border ${getColorClasses()}`}>
          {isEmpty && <AlertTriangle className="w-4 h-4" />}
          {!isEmpty && <Coins className="w-4 h-4" />}
          <div className="flex flex-col">
            <span className="text-sm font-medium leading-none">
              {tokens.toLocaleString()} tokens
            </span>
            {(isLow || isEmpty) && (
              <span className="text-xs opacity-75 leading-none mt-0.5">
                {isEmpty ? 'No tokens left' : 'Running low'}
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="hidden md:flex flex-col space-y-1 min-w-[80px]">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>0</span>
            <span>{maxTokens.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Warning tooltip for low tokens */}
      {isLow && !isEmpty && (
        <div className="hidden lg:block relative group">
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            You're running low on tokens
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenMeter;
